(() => {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class InteractivePatternIntro extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.stories = Array.from(this.querySelectorAll('[data-story-index]'));
      this.storyMedia = Array.from(this.querySelectorAll('[data-story-media-index]'));
      this.paletteButtons = Array.from(this.querySelectorAll('[data-pattern-palette]'));
      this.currentStep = this.querySelector('[data-pattern-step-current]');
      this.nextButton = this.querySelector('[data-pattern-intro-next]');
      this.autoPalette = this.dataset.autoPalette === 'true';
      this.designMode = this.dataset.designMode === 'true';
      this.manualPalette = false;
      this.activeStory = -1;
      this.activePalette = -1;
      this.frameRequested = false;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.handleViewportChange = this.requestUpdate.bind(this);
      this.handlePointerMove = this.onPointerMove.bind(this);
      this.handlePointerLeave = this.onPointerLeave.bind(this);
      this.handleBlockSelect = this.onBlockSelect.bind(this);

      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });
      this.addEventListener('pointermove', this.handlePointerMove, { passive: true });
      this.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.paletteButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.manualPalette = true;
          this.setPalette(Number(button.dataset.patternPalette));
        });
      });

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.scrollToProduct());
      }

      this.setPalette(1);

      if (this.reducedMotion) {
        this.stories.forEach((story) => story.setAttribute('aria-hidden', 'false'));
        if (this.currentStep) this.currentStep.textContent = '01';
      } else {
        this.setStory(0);
      }

      this.requestUpdate();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
      this.removeEventListener('pointermove', this.handlePointerMove);
      this.removeEventListener('pointerleave', this.handlePointerLeave);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }

    requestUpdate() {
      if (this.frameRequested) return;

      this.frameRequested = true;

      window.requestAnimationFrame(() => {
        this.frameRequested = false;
        this.updateFromScroll();
      });
    }

    updateFromScroll() {
      if (this.reducedMotion || this.designMode) {
        this.style.setProperty('--pi-progress', '1');
        return;
      }

      const rect = this.getBoundingClientRect();
      const scrollableDistance = Math.max(this.offsetHeight - window.innerHeight, 1);
      const progress = clamp(-rect.top / scrollableDistance, 0, 1);
      const storyCount = Math.max(this.stories.length, 1);
      const storyIndex = Math.min(
        storyCount - 1,
        Math.floor(progress * storyCount)
      );

      this.style.setProperty('--pi-progress', progress.toFixed(4));
      this.setStory(storyIndex);

      if (this.autoPalette && !this.manualPalette) {
        this.setPalette((storyIndex % 3) + 1);
      }
    }

    setStory(index) {
      if (!this.stories.length || index === this.activeStory) return;

      this.activeStory = index;

      this.stories.forEach((story, storyIndex) => {
        const active = storyIndex === index;
        story.classList.toggle('is-active', active);
        story.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.storyMedia.forEach((media, mediaIndex) => {
        const active = mediaIndex === index;
        media.classList.toggle('is-active', active);
        media.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (this.currentStep) {
        this.currentStep.textContent = String(index + 1).padStart(2, '0');
      }
    }

    setPalette(index) {
      if (index === this.activePalette || index < 1 || index > 3) return;

      this.activePalette = index;

      this.style.setProperty('--pi-bg', `var(--pi-bg-${index})`);
      this.style.setProperty('--pi-fg', `var(--pi-fg-${index})`);
      this.style.setProperty('--pi-accent', `var(--pi-accent-${index})`);

      this.paletteButtons.forEach((button) => {
        const active = Number(button.dataset.patternPalette) === index;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    onPointerMove(event) {
      if (this.reducedMotion || event.pointerType === 'touch') return;

      const rect = this.getBoundingClientRect();
      const x = clamp(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -1,
        1
      );
      const y = clamp(
        ((event.clientY - rect.top) /
          Math.min(rect.height, window.innerHeight)) *
          2 -
          1,
        -1,
        1
      );

      this.style.setProperty('--pi-pointer-x', x.toFixed(3));
      this.style.setProperty('--pi-pointer-y', y.toFixed(3));
    }

    onPointerLeave() {
      this.style.setProperty('--pi-pointer-x', '0');
      this.style.setProperty('--pi-pointer-y', '0');
    }

    onBlockSelect(event) {
      if (!this.designMode || !event.detail?.blockId) return;

      const selectedIndex = this.stories.findIndex(
        (story) => story.dataset.blockId === event.detail.blockId
      );

      if (selectedIndex >= 0) this.setStory(selectedIndex);
    }

    scrollToProduct() {
      const sectionWrapper = this.closest('.shopify-section');
      const nextSection = sectionWrapper?.nextElementSibling;

      if (nextSection) {
        nextSection.scrollIntoView({
          behavior: this.reducedMotion ? 'auto' : 'smooth',
          block: 'start',
        });
      }
    }
  }

  if (!customElements.get('interactive-pattern-intro')) {
    customElements.define(
      'interactive-pattern-intro',
      InteractivePatternIntro
    );
  }
})();